import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Download,
  Filter,
  Calendar,
} from "lucide-react";
import { API_URL } from "../components/config";
import NotificationAnalyticsCard from "./StatsComponents/NotificationAnalyticsCard";

const ITEMS_PER_PAGE = 6;
const PRIMARY_COLOR = "#002966";
const SECONDARY_COLOR = "#ffc000";

const StsDashboard = () => {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [timeRange, setTimeRange] = useState("week");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [firstDate, setFirstDate] = useState("2024-12-01");
  const [secondDate, setSecondDate] = useState("2025-03-14");
  const handlePageChange = (direction) => {
    setCurrentPage((prev) => {
      if (direction === "next") return (prev + 1) % totalPages;
      return prev === 0 ? totalPages - 1 : prev - 1;
    });
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchStatistics(), fetchAppStats()]);
    setIsRefreshing(false);
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch(
        `${API_URL}/application_statistics/get_last_statistic`
      );
      if (!response.ok) throw new Error("Failed to fetch statistics");
      const data = await response.json();
      setStatistics(data[0]);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchAppStats = async () => {
    try {
      const response = await fetch(
        `${API_URL}/application_statistics/get_app_stats_by_dates/${firstDate}/${secondDate}`
      );
      if (!response.ok) throw new Error("Failed to fetch app stats");
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchStatistics();
    fetchAppStats();
    setLoading(false);
  }, []);

  const calculateGrowthRate = (current, previous) => {
    if (!previous) return 0;
    return ((current - previous) / previous) * 100;
  };

  const filteredStatistics = useMemo(() => {
    if (!statistics) return [];
    return Object.entries(statistics).map(([key, value]) => ({
      key,
      value,
      growthRate: calculateGrowthRate(value, value * 0.9), // Simulated previous value
    }));
  }, [statistics]);

  const currentEntries = filteredStatistics.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredStatistics.length / ITEMS_PER_PAGE);

  const formatLabel = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatValue = (key, value) => {
    if (typeof value === "number") {
      if (key.includes("income") || key.includes("commission")) {
        return `$${value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      }
      return value.toLocaleString();
    }
    return value;
  };

  const getCardColor = (growthRate) => {
    return growthRate >= 0 ? PRIMARY_COLOR : "#dc2626";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <RefreshCw size={40} className="text-primary" />
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen text-red-600">
        <p>Error: {error}</p>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${
        isDarkMode ? "bg-gray-900" : "bg-gray-50"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap justify-between items-center mb-8 gap-4">
          <h1
            className={`text-3xl font-bold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Application Statistics Dashboard
          </h1>
          <div className="flex gap-4">
            <input
              type="date"
              value={firstDate}
              onChange={(e) => setFirstDate(e.target.value)}
              className={`px-4 py-2 rounded-lg ${
                isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
              } shadow-lg`}
            />
            <input
              type="date"
              value={secondDate}
              onChange={(e) => setSecondDate(e.target.value)}
              className={`px-4 py-2 rounded-lg ${
                isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
              } shadow-lg`}
            />
            <button
              onClick={refreshData}
              className={`px-4 py-2 rounded-lg bg-${PRIMARY_COLOR} text-white shadow-lg flex items-center gap-2`}
            >
              <RefreshCw
                className={`${isRefreshing ? "animate-spin" : ""}`}
                size={18}
              />
              Refresh
            </button>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`px-4 py-2 rounded-lg ${
                isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
              } shadow-lg`}
            >
              {isDarkMode ? "🌞 Light Mode" : "🌙 Dark Mode"}
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {currentEntries.map(({ key, value, growthRate }, index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`p-6 rounded-xl backdrop-blur-sm ${
                  isDarkMode ? "bg-gray-800/90" : "bg-white/90"
                } shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
                style={{
                  borderLeft: `4px solid ${getCardColor(growthRate)}`,
                }}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3
                    className={`text-lg font-medium ${
                      isDarkMode ? "text-white" : "text-gray-800"
                    }`}
                  >
                    {formatLabel(key)}
                  </h3>
                  <div
                    className={`flex items-center gap-1 ${
                      growthRate >= 0 ? "text-green-500" : "text-red-500"
                    }`}
                  >
                    {growthRate >= 0 ? (
                      <TrendingUp size={16} />
                    ) : (
                      <TrendingDown size={16} />
                    )}
                    <span className="text-sm font-medium">
                      {Math.abs(growthRate).toFixed(1)}%
                    </span>
                  </div>
                </div>
                <p
                  className={`text-2xl font-bold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {formatValue(key, value)}
                </p>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <div className="flex justify-center mt-6 gap-4 p-4">
          <button
            onClick={() => handlePageChange("prev")}
            className={`p-2 rounded-full ${
              isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            } shadow-lg hover:bg-${SECONDARY_COLOR} hover:text-white transition-colors`}
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={() => handlePageChange("next")}
            className={`p-2 rounded-full ${
              isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            } shadow-lg hover:bg-${SECONDARY_COLOR} hover:text-white transition-colors`}
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`p-6 rounded-xl shadow-lg ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h2
              className={`text-xl font-bold mb-4 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              User Growth Trends
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <defs>
                  <linearGradient
                    id="colorCustomers"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={PRIMARY_COLOR}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={PRIMARY_COLOR}
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                  <linearGradient id="colorDrivers" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={SECONDARY_COLOR}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={SECONDARY_COLOR}
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={isDarkMode ? "#374151" : "#e5e7eb"}
                />
                <XAxis
                  dataKey="datefor"
                  stroke={isDarkMode ? "#fff" : "#000"}
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis stroke={isDarkMode ? "#fff" : "#000"} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                    border: "none",
                    borderRadius: "8px",
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="customers_count"
                  name="Customers"
                  stroke={PRIMARY_COLOR}
                  fillOpacity={1}
                  fill="url(#colorCustomers)"
                />
                <Area
                  type="monotone"
                  dataKey="drivers_count"
                  name="Drivers"
                  stroke={SECONDARY_COLOR}
                  fillOpacity={1}
                  fill="url(#colorDrivers)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`p-6 rounded-xl shadow-lg ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h2
              className={`text-xl font-bold mb-4 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Revenue Distribution
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    {
                      name: "Company Income",
                      value: statistics?.company_income_from_charges || 0,
                    },
                    {
                      name: "Promotions",
                      value: statistics?.company_promotions || 0,
                    },
                    {
                      name: "Commission",
                      value: statistics?.total_commission || 0,
                    },
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill={PRIMARY_COLOR}
                  paddingAngle={5}
                  dataKey="value"
                >
                  <Cell fill={PRIMARY_COLOR} />
                  <Cell fill={SECONDARY_COLOR} />
                  <Cell fill="#4B5563" />
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                    border: "none",
                    borderRadius: "8px",
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`p-6 rounded-xl shadow-lg mt-6 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <h2
            className={`text-xl font-bold mb-4 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Trip Analytics
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={isDarkMode ? "#374151" : "#e5e7eb"}
              />
              <XAxis dataKey="datefor" stroke={isDarkMode ? "#fff" : "#000"} />
              <YAxis stroke={isDarkMode ? "#fff" : "#000"} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                  border: "none",
                  borderRadius: "8px",
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="trips_count"
                name="Total Trips"
                stroke={PRIMARY_COLOR}
                strokeWidth={2}
                dot={{ fill: PRIMARY_COLOR }}
                activeDot={{ r: 8 }}
              />
              <Line
                type="monotone"
                dataKey="kms_billed"
                name="Distance (km)"
                stroke={SECONDARY_COLOR}
                strokeWidth={2}
                dot={{ fill: SECONDARY_COLOR }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Notification Analytics Card */}
        <NotificationAnalyticsCard />
      </div>
    </div>
  );
};

export default StsDashboard;
